import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import BackupRoundedIcon from "@mui/icons-material/BackupRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import api from "services/api";

/**
 * Server overview (2026-10-21).
 *
 * Reads ONLY /server/* endpoints — the web process never shells out for
 * metrics. All values come from snapshots written by the system-cron
 * collector, so this page still renders even when the host is unhealthy.
 */

const formatPercent = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(1)}%`;

const formatGB = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(1)} GB`;

const formatMB = (value) =>
  value === null || value === undefined ? "—" : `${Math.round(Number(value))} MB`;

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const total = Math.floor(Number(seconds));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const relativeTime = (isoValue) => {
  if (!isoValue) return "never";
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(isoValue).getTime()) / 1000)
  );
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const severityColor = (theme, severity) =>
  severity === "critical" ? theme.palette.error.main : theme.palette.warning.main;

// ── Presentational pieces ───────────────────────────────────────────────────

const MetricCard = ({ icon, title, value, subtitle, progress, children }) => {
  const theme = useTheme();

  const progressColor =
    progress === null || progress === undefined
      ? theme.palette.primary.main
      : progress >= 92
        ? theme.palette.error.main
        : progress >= 85
          ? theme.palette.warning.main
          : theme.palette.primary.main;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ color: theme.palette.primary.main, display: "flex" }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      {value !== undefined && (
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
          {value}
        </Typography>
      )}

      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}

      {progress !== null && progress !== undefined && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, Number(progress)))}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: theme.palette.action.hover,
            "& .MuiLinearProgress-bar": { bgcolor: progressColor, borderRadius: 4 },
          }}
        />
      )}

      {children}
    </Paper>
  );
};

const StatusChip = ({ status }) => {
  const theme = useTheme();

  const config =
    status === "running"
      ? { label: "Running", color: theme.palette.success.main, icon: <CheckCircleRoundedIcon /> }
      : status === "stopped"
        ? { label: "Stopped", color: theme.palette.error.main, icon: <ErrorRoundedIcon /> }
        : { label: "Unknown", color: theme.palette.warning.main, icon: <WarningRoundedIcon /> };

  return (
    <Chip
      size="small"
      icon={config.icon}
      label={config.label}
      sx={{
        height: 24,
        borderRadius: "8px",
        fontWeight: 600,
        bgcolor: `${config.color}1a`,
        color: config.color,
        "& .MuiChip-icon": { color: config.color, fontSize: 16 },
      }}
    />
  );
};

/** Dependency-free sparkline — 24h trend without pulling in a chart engine. */
const Sparkline = ({ values, height = 36 }) => {
  const theme = useTheme();
  const points = (values || []).filter((value) => value !== null && value !== undefined);

  const geometry = useMemo(() => {
    if (points.length < 2) return null;
    const max = 100;
    const min = 0;
    const width = 100;
    return points
      .map((value, index) => {
        const x = (index / (points.length - 1)) * width;
        const y = height - ((Number(value) - min) / (max - min)) * height;
        return `${x.toFixed(2)},${Math.max(0, Math.min(height, y)).toFixed(2)}`;
      })
      .join(" ");
  }, [points, height]);

  if (!geometry) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height }}
        aria-hidden="true"
      >
        <polyline
          points={geometry}
          fill="none"
          stroke={theme.palette.primary.main}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

const ServerOverview = () => {
  const theme = useTheme();

  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [database, setDatabase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [statusResponse, configResponse, historyResponse, databaseResponse] =
        await Promise.all([
          api.get("/server/status"),
          api.get("/server/config"),
          api.get("/server/history", { params: { hours: 24 } }),
          // Database stats are secondary — never let a failure here break the page.
          api.get("/server/database").catch(() => null),
        ]);

      setStatus(statusResponse.data || null);
      setConfig(configResponse.data || null);
      setHistory(historyResponse.data?.series || []);
      setDatabase(databaseResponse?.data || null);
      setError("");
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error || "Failed to load server status."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/server/refresh");
      await loadAll();
    } catch (refreshError) {
      setError(
        refreshError?.response?.data?.detail ||
          refreshError?.response?.data?.error ||
          "Failed to collect server metrics."
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const snapshot = status?.snapshot || null;
  const staleness = status?.staleness || {};
  const issues = status?.issues || [];
  const counts = status?.counts || { critical: 0, warning: 0 };
  const healthStatus = status?.healthStatus || "unknown";

  const services = Array.isArray(snapshot?.Services) ? snapshot.Services : [];
  const certificates = Array.isArray(snapshot?.SslCertificates)
    ? snapshot.SslCertificates
    : [];
  const backups = snapshot?.Backups || null;

  const banner =
    healthStatus === "critical"
      ? {
          color: theme.palette.error.main,
          icon: <ErrorRoundedIcon />,
          title: `${counts.critical} critical issue${counts.critical === 1 ? "" : "s"} need attention`,
        }
      : healthStatus === "warning"
        ? {
            color: theme.palette.warning.main,
            icon: <WarningRoundedIcon />,
            title: `${counts.warning} warning${counts.warning === 1 ? "" : "s"} to review`,
          }
        : healthStatus === "ok"
          ? {
              color: theme.palette.success.main,
              icon: <CheckCircleRoundedIcon />,
              title: "All systems nominal",
            }
          : {
              color: theme.palette.grey[500],
              icon: <WarningRoundedIcon />,
              title: "No data collected yet",
            };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 2 }}>
      {/* Health banner ------------------------------------------------------ */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          bgcolor: `${banner.color}12`,
          borderColor: `${banner.color}55`,
        }}
      >
        <Box sx={{ color: banner.color, display: "flex" }}>{banner.icon}</Box>

        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: banner.color }}>
            {banner.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last collected {relativeTime(snapshot?.CapturedAt)}
            {snapshot?.Hostname ? ` · ${snapshot.Hostname}` : ""}
            {snapshot?.CollectedBy ? ` · ${snapshot.CollectedBy}` : ""}
            {staleness.isStale ? " · collector may be down" : ""}
          </Typography>
        </Box>

        <Button
          size="small"
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshRoundedIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
        >
          {refreshing ? "Collecting…" : "Refresh now"}
        </Button>
      </Paper>

      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      {/* Issue list --------------------------------------------------------- */}
      {issues.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Issues
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {issues.map((issue, index) => (
              <Box key={`${issue.code}-${index}`} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Box sx={{ color: severityColor(theme, issue.severity), display: "flex", mt: "2px" }}>
                  {issue.severity === "critical" ? (
                    <ErrorRoundedIcon fontSize="small" />
                  ) : (
                    <WarningRoundedIcon fontSize="small" />
                  )}
                </Box>
                <Typography variant="body2">{issue.message}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Metric cards ------------------------------------------------------- */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<MemoryRoundedIcon />}
            title="CPU"
            value={formatPercent(snapshot?.CpuPercent)}
            subtitle={
              snapshot
                ? `${snapshot.CpuCores ?? "?"} cores · load ${snapshot.LoadAvg1 ?? "—"} / ${snapshot.LoadAvg5 ?? "—"} / ${snapshot.LoadAvg15 ?? "—"}`
                : "No data"
            }
            progress={snapshot?.CpuPercent}
          >
            <Sparkline values={history.map((point) => point.cpu)} />
          </MetricCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<DnsRoundedIcon />}
            title="Memory"
            value={formatPercent(snapshot?.MemoryPercent)}
            subtitle={
              snapshot
                ? `${formatMB(snapshot.MemoryUsedMB)} of ${formatMB(snapshot.MemoryTotalMB)}${
                    snapshot.SwapTotalMB
                      ? ` · swap ${formatMB(snapshot.SwapUsedMB)} / ${formatMB(snapshot.SwapTotalMB)}`
                      : ""
                  }`
                : "No data"
            }
            progress={snapshot?.MemoryPercent}
          >
            <Sparkline values={history.map((point) => point.memory)} />
          </MetricCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<StorageRoundedIcon />}
            title="Disk"
            value={formatPercent(snapshot?.DiskPercent)}
            subtitle={
              snapshot
                ? `${formatGB(snapshot.DiskUsedGB)} of ${formatGB(snapshot.DiskTotalGB)} · ${snapshot.DiskMount || "/"}`
                : "No data"
            }
            progress={snapshot?.DiskPercent}
          >
            <Sparkline values={history.map((point) => point.disk)} />
          </MetricCard>
        </Grid>

        {/* Services --------------------------------------------------------- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard icon={<DnsRoundedIcon />} title="Services">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
              {services.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No services probed.
                </Typography>
              )}
              {services.map((service) => (
                <Box
                  key={service.name}
                  sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 70 }}>
                    {service.name}
                  </Typography>
                  <StatusChip status={service.status} />
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 140 }}>
                    {service.detail}
                  </Typography>
                </Box>
              ))}
            </Box>
          </MetricCard>
        </Grid>

        {/* SSL -------------------------------------------------------------- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard icon={<LockRoundedIcon />} title="SSL certificates">
            {!config?.sslConfigured && (
              <Typography variant="body2" color="text.secondary">
                Not configured. Add <code>SERVER_SSL_CERTS</code> to the server
                environment to monitor certificate expiry.
              </Typography>
            )}

            {config?.sslConfigured && certificates.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No certificate data in the latest snapshot.
              </Typography>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {certificates.map((certificate) => {
                const color =
                  certificate.daysRemaining === null || certificate.status === "error"
                    ? theme.palette.grey[500]
                    : certificate.daysRemaining < 0
                      ? theme.palette.error.main
                      : certificate.daysRemaining <= 14
                        ? theme.palette.warning.main
                        : theme.palette.success.main;

                return (
                  <Box key={certificate.domain} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {certificate.domain}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          certificate.status === "error"
                            ? "Error"
                            : certificate.daysRemaining < 0
                              ? "Expired"
                              : `${certificate.daysRemaining} days left`
                        }
                        sx={{
                          height: 22,
                          borderRadius: "8px",
                          fontWeight: 600,
                          bgcolor: `${color}1a`,
                          color,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {certificate.notAfter
                        ? `Expires ${new Date(certificate.notAfter).toLocaleDateString()}${certificate.issuer ? ` · ${certificate.issuer}` : ""}`
                        : certificate.error || "Expiry unknown"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </MetricCard>
        </Grid>

        {/* Backups ---------------------------------------------------------- */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<BackupRoundedIcon />}
            title="DB backups"
            value={
              backups?.latest
                ? `${backups.latest.ageHours}h old`
                : backups?.status === "unavailable"
                  ? "—"
                  : "None"
            }
            subtitle={
              backups?.latest
                ? `${backups.latest.name} · ${(backups.latest.sizeBytes / 1024 / 1024).toFixed(1)} MB · ${backups.fileCount ?? 0} file(s)`
                : backups?.error || backups?.directory || ""
            }
          >
            {backups?.status && (
              <Chip
                size="small"
                label={backups.status}
                sx={{
                  alignSelf: "flex-start",
                  height: 22,
                  borderRadius: "8px",
                  fontWeight: 600,
                  textTransform: "capitalize",
                  bgcolor:
                    backups.status === "ok"
                      ? `${theme.palette.success.main}1a`
                      : backups.status === "warning"
                        ? `${theme.palette.warning.main}1a`
                        : `${theme.palette.error.main}1a`,
                  color:
                    backups.status === "ok"
                      ? theme.palette.success.main
                      : backups.status === "warning"
                        ? theme.palette.warning.main
                        : theme.palette.error.main,
                }}
              />
            )}
          </MetricCard>
        </Grid>

        {/* Uptime ----------------------------------------------------------- */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard
            icon={<ScheduleRoundedIcon />}
            title="Host uptime"
            value={formatDuration(snapshot?.UptimeSeconds)}
            subtitle={
              snapshot?.CapturedAt
                ? `Snapshot taken ${new Date(snapshot.CapturedAt).toLocaleString()}`
                : "No data"
            }
          />
        </Grid>

        {/* Database summary — the full breakdown lives in the Database tab */}
        {database?.totals && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              icon={<TableChartRoundedIcon />}
              title="Database"
              value={`${Number(database.totals.tables).toLocaleString()} tables`}
              subtitle={`~${Number(
                database.totals.rowEstimate
              ).toLocaleString()} rows · ${(
                Number(database.totals.totalBytes) /
                1024 /
                1024
              ).toFixed(1)} MB · ${database.database || ""}`}
            >
              <Typography variant="caption" color="text.secondary">
                {database.pool
                  ? `Pool: ${database.pool.using ?? 0} in use · ${database.pool.available ?? 0} idle${
                      database.pool.waiting > 0
                        ? ` · ${database.pool.waiting} waiting`
                        : ""
                    }`
                  : "See the Database tab for the per-table breakdown."}
              </Typography>
            </MetricCard>
          </Grid>
        )}
      </Grid>

      <Divider />
      <Typography variant="caption" color="text.secondary">
        Metrics are collected by the server's cron job and stored in the Portal
        database. This page only reads the stored snapshots.
        {config?.metricsRetentionDays
          ? ` Raw snapshots are kept for ${config.metricsRetentionDays} days.`
          : ""}
      </Typography>
    </Box>
  );
};

export default ServerOverview;
